/** Workspace Graph AI extraction settings and activity. */

import { useEffect, useMemo, useState } from 'react'
import { Activity, Database, Play, Save } from 'lucide-react'
import { AI_PROVIDERS, GRAPH_EXTRACTION_CREDIT_COST } from '@agentwiki/shared'
import { cn } from '../../lib/utils'
import { useAISettings } from '../../hooks/use-ai-settings'
import {
  useBackfillGraphExtraction,
  useGraphExtractionJobs,
  useGraphExtractionSettings,
  useGraphExtractionStatus,
  useUpdateGraphExtractionSettings,
} from '../../hooks/use-graph-extraction'
import type { GraphExtractionProviderMode } from '@agentwiki/shared'

export function GraphAISettingsTab({ isDark }: { isDark: boolean }) {
  const { data } = useGraphExtractionSettings()
  const { data: aiData } = useAISettings()
  const { data: status } = useGraphExtractionStatus()
  const { data: jobsData } = useGraphExtractionJobs(20)
  const updateSettings = useUpdateGraphExtractionSettings()
  const backfill = useBackfillGraphExtraction()

  const settings = data?.settings
  const byokProviders = useMemo(
    () => (aiData?.settings ?? []).filter((s) => s.isEnabled).sort((a, b) => a.priority - b.priority),
    [aiData?.settings],
  )

  const [enabled, setEnabled] = useState(true)
  const [autoRunOnSave, setAutoRunOnSave] = useState(true)
  const [providerMode, setProviderMode] = useState<GraphExtractionProviderMode>('cloudflare')
  const [cloudflareModel, setCloudflareModel] = useState('')
  const [byokProviderId, setByokProviderId] = useState<string | null>(null)
  const [byokModel, setByokModel] = useState<string | null>(null)

  useEffect(() => {
    if (!settings) return
    setEnabled(settings.enabled)
    setAutoRunOnSave(settings.autoRunOnSave)
    setProviderMode(settings.providerMode)
    setCloudflareModel(settings.cloudflareModel)
    setByokProviderId(settings.byokProviderId ?? null)
    setByokModel(settings.byokModel ?? null)
  }, [settings])

  const selectedProvider = byokProviders.find((p) => p.providerId === byokProviderId) ?? byokProviders[0]
  const providerModels = selectedProvider
    ? (AI_PROVIDERS[selectedProvider.providerId]?.models as readonly string[] | undefined) ?? [selectedProvider.defaultModel]
    : []

  const panelCls = cn('rounded-lg border p-4', isDark ? 'border-white/[0.06] bg-surface-1' : 'border-neutral-200 bg-white')
  const labelCls = cn('text-xs font-medium', isDark ? 'text-neutral-400' : 'text-neutral-500')
  const inputCls = cn(
    'w-full rounded border px-2.5 py-1.5 text-xs outline-none',
    isDark ? 'border-white/[0.06] bg-surface-2 text-neutral-200' : 'border-neutral-200 bg-neutral-50 text-neutral-800',
  )

  const save = () => updateSettings.mutate({
    enabled,
    autoRunOnSave,
    providerMode,
    cloudflareModel,
    byokProviderId: providerMode === 'byok' ? byokProviderId : null,
    byokModel: providerMode === 'byok' ? byokModel : null,
  })

  return (
    <div className="space-y-5">
      <div>
        <h2 className={cn('text-sm font-semibold', isDark ? 'text-neutral-200' : 'text-neutral-800')}>Graph AI</h2>
        <p className={cn('mt-1 text-xs', isDark ? 'text-neutral-500' : 'text-neutral-400')}>
          Background extraction for entities, relationships, and document graph enrichment.
        </p>
      </div>

      <div className={cn(panelCls, 'space-y-4')}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className={cn('text-xs font-medium', isDark ? 'text-neutral-200' : 'text-neutral-800')}>Service</div>
            <div className={cn('mt-0.5 text-[11px]', isDark ? 'text-neutral-500' : 'text-neutral-400')}>
              Status: {status?.state ?? 'idle'} · {status?.succeeded ?? 0} succeeded · {status?.failed ?? 0} failed
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            <span className={isDark ? 'text-neutral-300' : 'text-neutral-700'}>Enabled</span>
          </label>
        </div>

        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={autoRunOnSave} onChange={(e) => setAutoRunOnSave(e.target.checked)} disabled={!enabled} />
          <span className={isDark ? 'text-neutral-300' : 'text-neutral-700'}>Run automatically on document save</span>
        </label>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className={labelCls}>Provider mode</label>
            <select value={providerMode} onChange={(e) => setProviderMode(e.target.value as GraphExtractionProviderMode)} className={cn(inputCls, 'mt-1')}>
              <option value="cloudflare">Cloudflare Workers AI</option>
              <option value="byok">BYOK provider</option>
            </select>
          </div>

          {providerMode === 'cloudflare' ? (
            <div>
              <label className={labelCls}>Cloudflare model</label>
              <select value={cloudflareModel} onChange={(e) => setCloudflareModel(e.target.value)} className={cn(inputCls, 'mt-1')}>
                {(data?.cloudflareModels ?? []).map((m) => (
                  <option key={m.id} value={m.id}>{m.name} · {Math.round(m.contextTokens / 1000)}k ctx</option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label className={labelCls}>BYOK provider</label>
              <select
                value={byokProviderId ?? selectedProvider?.providerId ?? ''}
                onChange={(e) => {
                  const next = e.target.value || null
                  setByokProviderId(next)
                  const provider = byokProviders.find((p) => p.providerId === next)
                  setByokModel(provider?.defaultModel ?? null)
                }}
                className={cn(inputCls, 'mt-1')}
              >
                {byokProviders.length === 0 && <option value="">No enabled provider</option>}
                {byokProviders.map((p) => (
                  <option key={p.providerId} value={p.providerId}>{AI_PROVIDERS[p.providerId]?.name ?? p.providerId}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {providerMode === 'byok' && providerModels.length > 0 && (
          <div>
            <label className={labelCls}>BYOK model</label>
            <select value={byokModel ?? selectedProvider?.defaultModel ?? providerModels[0]} onChange={(e) => setByokModel(e.target.value)} className={cn(inputCls, 'mt-1')}>
              {providerModels.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        )}

        <div className={cn('rounded border px-3 py-2 text-[11px]', isDark ? 'border-white/[0.06] text-neutral-400' : 'border-neutral-200 text-neutral-500')}>
          Cloudflare extraction deducts {GRAPH_EXTRACTION_CREDIT_COST} credits after a successful processed extraction. BYOK extraction uses your API key and deducts 0 AgentWiki credits.
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={save}
            disabled={updateSettings.isPending}
            className="inline-flex cursor-pointer items-center gap-1.5 rounded bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            <Save size={13} /> {updateSettings.isPending ? 'Saving...' : 'Save'}
          </button>
          <button
            onClick={() => backfill.mutate({ force: false })}
            disabled={backfill.isPending || !enabled}
            className={cn(
              'inline-flex cursor-pointer items-center gap-1.5 rounded border px-3 py-1.5 text-xs font-medium disabled:opacity-50',
              isDark ? 'border-white/[0.08] text-neutral-300 hover:bg-surface-2' : 'border-neutral-200 text-neutral-700 hover:bg-neutral-50',
            )}
          >
            <Play size={13} /> Backfill workspace
          </button>
        </div>
      </div>

      <div className={cn(panelCls, 'space-y-3')}>
        <div className="flex items-center gap-2">
          <Activity className={cn('h-4 w-4', isDark ? 'text-neutral-500' : 'text-neutral-400')} />
          <h3 className={cn('text-xs font-semibold uppercase tracking-wide', isDark ? 'text-neutral-400' : 'text-neutral-500')}>Activity</h3>
        </div>
        {(jobsData?.jobs ?? []).length === 0 ? (
          <div className={cn('flex items-center gap-2 py-4 text-xs', isDark ? 'text-neutral-500' : 'text-neutral-400')}>
            <Database size={14} /> No extraction activity yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className={isDark ? 'text-neutral-500' : 'text-neutral-400'}>
                  <th className="py-1 text-left font-medium">Status</th>
                  <th className="py-1 text-left font-medium">Trigger</th>
                  <th className="py-1 text-left font-medium">Model</th>
                  <th className="py-1 text-right font-medium">Credits</th>
                  <th className="py-1 text-right font-medium">When</th>
                </tr>
              </thead>
              <tbody>
                {(jobsData?.jobs ?? []).map((job) => (
                  <tr key={job.id} className={isDark ? 'border-t border-white/[0.04]' : 'border-t border-neutral-100'}>
                    <td className={cn('py-2', job.status === 'failed' ? 'text-red-400' : isDark ? 'text-neutral-300' : 'text-neutral-700')}>{job.status}</td>
                    <td className={cn('py-2', isDark ? 'text-neutral-400' : 'text-neutral-500')}>{job.trigger}</td>
                    <td className={cn('max-w-[160px] truncate py-2 font-mono', isDark ? 'text-neutral-500' : 'text-neutral-500')}>{job.model}</td>
                    <td className={cn('py-2 text-right', isDark ? 'text-neutral-400' : 'text-neutral-500')}>{job.creditsCharged}</td>
                    <td className={cn('py-2 text-right', isDark ? 'text-neutral-500' : 'text-neutral-400')}>{new Date(job.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
